export type HeldNote = {
  source: string;
  midi: number;
  velocity: number;
  /** 1 for keys and touch. MIDI note-on velocity maps below 1, and never above it. */
  breathScale: number;
  scope?: string;
  pressed: boolean;
};

/** Last-note priority, with ownership independent of pitch. One horn, many inputs. */
export class HornPerformance {
  private notes = new Map<string, HeldNote>();
  private pedals = new Set<string>();
  private bends = new Map<string, number>();

  get current(): HeldNote | undefined {
    return [...this.notes.values()].at(-1);
  }
  get pitches(): ReadonlySet<number> {
    const pitches = new Set<number>();
    for (const note of this.notes.values()) {
      pitches.delete(note.midi);
      pitches.add(note.midi);
    }
    return pitches;
  }
  get bend(): number {
    const scope = this.current?.scope;
    return scope ? (this.bends.get(scope) ?? 0) : 0;
  }
  noteOf(source: string) {
    return this.notes.get(source);
  }
  /** Held notes in the order they were most recently struck, including pedal holds. */
  list(): HeldNote[] {
    return [...this.notes.values()];
  }
  down(source: string, midi: number, velocity: number, scope?: string, breathScale = 1) {
    if (!Number.isInteger(midi) || midi < 0 || midi > 127) return;
    this.notes.delete(source);
    this.notes.set(source, {
      source,
      midi,
      velocity: Number.isFinite(velocity) ? Math.max(0, Math.min(1, velocity)) : 0.7,
      breathScale: Number.isFinite(breathScale) ? Math.max(0, Math.min(1, breathScale)) : 1,
      scope,
      pressed: true,
    });
  }
  up(source: string) {
    const note = this.notes.get(source);
    if (!note) return;
    if (note.scope && this.pedals.has(note.scope)) note.pressed = false;
    else this.notes.delete(source);
  }
  sustain(scope: string, down: boolean) {
    if (down) this.pedals.add(scope);
    else {
      this.pedals.delete(scope);
      for (const [source, note] of this.notes) {
        if (note.scope === scope && !note.pressed) this.notes.delete(source);
      }
    }
  }
  setBend(scope: string, cents: number) {
    if (Number.isFinite(cents)) this.bends.set(scope, Math.max(-200, Math.min(200, cents)));
  }
  /** CC120/123 clear one channel; disconnect clears all channels of one device. */
  clearScope(prefix: string) {
    const matches = (scope: string) => scope === prefix || scope.startsWith(prefix + ":");
    for (const [source, note] of this.notes)
      if (note.scope && matches(note.scope)) this.notes.delete(source);
    for (const scope of this.pedals) if (matches(scope)) this.pedals.delete(scope);
    for (const scope of this.bends.keys()) if (matches(scope)) this.bends.delete(scope);
  }
  resetControllers(scope: string) {
    this.sustain(scope, false);
    this.bends.delete(scope);
  }
  clear() {
    this.notes.clear();
    this.pedals.clear();
    this.bends.clear();
  }
}

/** A new strike slurs from a different sounding pitch; the same pitch re-tongues (a horn can't slur into itself). */
export function slursFrom(sounding: HeldNote | undefined, midi: number) {
  return !!sounding && sounding.midi !== midi;
}

/**
 * Legato on keeps the current slur rules (including a held slur or a finger glide).
 * Legato off re-articulates every new note, overlapping ones included.
 */
export function noteLegato(
  enabled: boolean,
  sounding: HeldNote | undefined,
  midi: number,
  forced = false,
) {
  return enabled && (slursFrom(sounding, midi) || forced);
}

/**
 * MIDI note-on velocity (0..1) → breath/attack scale.
 * 127 is exactly today's full breath. Softer velocities sit below that, never above.
 * A small floor keeps a very light key from falling silent.
 */
export function midiBreathScale(velocity: number) {
  if (!Number.isFinite(velocity)) return 1;
  const v = Math.min(1, Math.max(0, velocity));
  if (v >= 1) return 1;
  const curved = Math.pow(v, 1.35);
  const scale = 0.28 + 0.72 * curved;
  return scale > 1 ? 1 : scale;
}

/**
 * MIDI CC21 → character. 0 is horn (−1), 64 is center (today's lip), 127 is bass (+1).
 * CC1 stays mute. CC2 and CC11 stay air. Pressure stays additive air.
 */
export function characterFromCc(controllerValue: number) {
  if (!Number.isFinite(controllerValue)) return 0;
  const cc = Math.max(0, Math.min(127, Math.round(controllerValue)));
  if (cc === 64) return 0;
  if (cc < 64) return (cc - 64) / 64;
  return (cc - 64) / 63;
}

/** MIDI channel pressure adds breath on top of the pad/CC air; pressure 0 returns to that air, never below it. */
export function breathWithPressure(air: number, pressure: number) {
  return air + (1 - air) * Math.max(0, Math.min(1, pressure));
}
